import type { MelodyTapRawGesture } from "./melody-tap.ts";
import type { MidiNote, MidiTimeSignature, PitchFrame } from "./music.ts";
import type { PitchEvidenceFrame, RhythmSlot } from "./rhythm-slots.ts";

export const DEFAULT_SONG_PPQ = 480;

export type InputMethod = "VOICE" | "RHYTHM" | "BOTH";
export type PassPreRoll = "COUNT_IN" | "LISTEN_1_BAR" | "LISTEN_2_BARS" | "LISTEN_4_BARS";
export type PassMergeMode = "APPEND" | "REPLACE_RANGE";
export type PassLengthBars = number | null;
export type PassSelectionMode = "SELECTION_ONLY" | "FROM_SELECTION" | null;

export type BarRange = {
  startBar: number;
  endBar: number;
};

export type BarSelection = BarRange & {
  awaitingEnd: boolean;
};

export type DeviceTimingOffsets = {
  inputTimingOffsetMs: number;
  audioOutputOffsetMs: number;
};

export const DEFAULT_DEVICE_TIMING_OFFSETS: DeviceTimingOffsets = {
  inputTimingOffsetMs: 0,
  audioOutputOffsetMs: 0,
};

export type MusicalClock = {
  bpm: number;
  ppq: number;
  timeSignature: MidiTimeSignature;
  regionStartTick: number;
  recordStartAudioTime: number;
  recordStartPerformanceMs: number;
  timingOffsets: DeviceTimingOffsets;
};

export type SongPosition = {
  absoluteTick: number;
  section: "PICKUP" | "SONG";
  bar: number;
  beat: number;
  subdivision: number;
};

export type PassTapEvidence = {
  id: number;
  rawStartTimeMs: number;
  rawEndTimeMs: number;
  rawStartTick: number;
  rawEndTick: number;
  deviceCorrectedStartTick: number;
  deviceCorrectedEndTick: number;
  quantizedStartTick: number;
  quantizedEndTick: number;
  startTick: number;
  endTick: number;
};

export type InputPass = {
  id: string;
  source: "ADD_PASS";
  startTick: number;
  endTick: number;
  playbackStartBar: number;
  recordStartBar: number;
  recordEndBar: number | null;
  untilStop: boolean;
  replacementRange: BarRange | null;
  inputMethod: InputMethod;
  lengthBars: PassLengthBars;
  preRoll: PassPreRoll;
  pickup: boolean;
  mergeMode: PassMergeMode;
  createdAt: string;
  bpm: number;
  ppq: number;
  timeSignature: MidiTimeSignature;
  audioBlob: Blob | null;
  audioOffsetMs: number;
  f0Evidence: PitchEvidenceFrame[];
  tapEvents: PassTapEvidence[];
  rhythmSlots: RhythmSlot[];
  createdNotes: MidiNote[];
};

export type InputPassDraft = {
  playbackStartBar: number;
  recordStartBar: number;
  recordEndBar: number | null;
  untilStop: boolean;
  replacementRange: BarRange | null;
  selectedRange: BarRange | null;
  selectionMode: PassSelectionMode;
  lengthBars: PassLengthBars;
  inputMethod: InputMethod;
  preRoll: PassPreRoll;
  pickup: boolean;
  mergeMode: PassMergeMode;
};

const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value));

export function normalizeBarRange(range: BarRange): BarRange {
  return {
    startBar: Math.max(0, Math.min(Math.floor(range.startBar), Math.floor(range.endBar))),
    endBar: Math.max(0, Math.max(Math.floor(range.startBar), Math.floor(range.endBar))),
  };
}

export function advanceBarSelection(selection: BarSelection | null, bar: number): BarSelection {
  const safeBar = Math.max(0, Math.floor(bar));
  if (!selection || !selection.awaitingEnd) return { startBar: safeBar, endBar: safeBar, awaitingEnd: true };
  const range = normalizeBarRange({ startBar: selection.startBar, endBar: safeBar });
  return { ...range, awaitingEnd: false };
}

export function preRollBarCount(preRoll: PassPreRoll): number {
  if (preRoll === "LISTEN_1_BAR") return 1;
  if (preRoll === "LISTEN_2_BARS") return 2;
  if (preRoll === "LISTEN_4_BARS") return 4;
  return 0;
}

export function buildInputPassDraft(input: {
  nextBar: number;
  selectedRange?: BarRange | null;
  manualStartBar?: number | null;
  lengthBars: PassLengthBars;
  inputMethod: InputMethod;
  preRoll: PassPreRoll;
  pickup: boolean;
  mergeMode: PassMergeMode;
  selectionMode?: PassSelectionMode;
}): InputPassDraft {
  const selectedRange = input.selectedRange ? normalizeBarRange(input.selectedRange) : null;
  const selectedBarCount = selectedRange ? selectedRange.endBar - selectedRange.startBar + 1 : 0;
  const hasMultiSelection = selectedBarCount > 1;
  const selectionMode = hasMultiSelection ? input.selectionMode ?? "SELECTION_ONLY" : null;
  const automaticStartBar = selectedRange?.startBar ?? input.nextBar;
  const recordStartBar = Math.max(0, Math.floor(selectionMode === "SELECTION_ONLY" ? automaticStartBar : input.manualStartBar ?? automaticStartBar));
  const lengthBars = selectionMode === "SELECTION_ONLY" ? selectedBarCount : input.lengthBars;
  const recordEndBar = lengthBars === null ? null : recordStartBar + Math.max(1, Math.floor(lengthBars)) - 1;
  const untilStop = recordEndBar === null;
  const replacementRange = input.mergeMode === "REPLACE_RANGE" && recordEndBar !== null
    ? { startBar: recordStartBar, endBar: recordEndBar }
    : null;
  return {
    playbackStartBar: Math.max(0, recordStartBar - preRollBarCount(input.preRoll)),
    recordStartBar,
    recordEndBar,
    untilStop,
    replacementRange,
    selectedRange,
    selectionMode,
    lengthBars,
    inputMethod: input.inputMethod,
    preRoll: input.preRoll,
    pickup: input.pickup,
    mergeMode: input.mergeMode,
  };
}

export function musicalBeatsPerBar(signature: MidiTimeSignature): number {
  const numerator = Math.max(1, signature.numerator);
  const denominator = Math.max(1, signature.denominator);
  return numerator * 4 / denominator;
}

export function ticksPerBar(signature: MidiTimeSignature, ppq = DEFAULT_SONG_PPQ): number {
  return Math.max(1, Math.round(musicalBeatsPerBar(signature) * ppq));
}

export function createMusicalClock(input: {
  bpm: number;
  ppq?: number;
  timeSignature?: MidiTimeSignature;
  regionStartTick?: number;
  recordStartAudioTime: number;
  recordStartPerformanceMs: number;
  timingOffsets?: Partial<DeviceTimingOffsets>;
}): MusicalClock {
  return {
    bpm: Math.max(1, input.bpm),
    ppq: Math.max(24, Math.round(input.ppq ?? DEFAULT_SONG_PPQ)),
    timeSignature: input.timeSignature ?? { numerator: 4, denominator: 4 },
    regionStartTick: Math.round(input.regionStartTick ?? 0),
    recordStartAudioTime: input.recordStartAudioTime,
    recordStartPerformanceMs: input.recordStartPerformanceMs,
    timingOffsets: {
      inputTimingOffsetMs: Number.isFinite(input.timingOffsets?.inputTimingOffsetMs) ? input.timingOffsets?.inputTimingOffsetMs ?? 0 : 0,
      audioOutputOffsetMs: Number.isFinite(input.timingOffsets?.audioOutputOffsetMs) ? input.timingOffsets?.audioOutputOffsetMs ?? 0 : 0,
    },
  };
}

export function audioTimeToAbsoluteTick(clock: MusicalClock, audioTime: number): number {
  return clock.regionStartTick + Math.round((audioTime - clock.recordStartAudioTime) * clock.bpm / 60 * clock.ppq);
}

export function performanceTimeToAbsoluteTick(clock: MusicalClock, performanceMs: number): number {
  return clock.regionStartTick + Math.round((performanceMs - clock.recordStartPerformanceMs) / 1000 * clock.bpm / 60 * clock.ppq);
}

export function correctedPerformanceTimeToAbsoluteTick(clock: MusicalClock, performanceMs: number): number {
  return performanceTimeToAbsoluteTick(clock, performanceMs - clock.timingOffsets.inputTimingOffsetMs);
}

export function tickToSongPosition(tick: number, signature: MidiTimeSignature, ppq = DEFAULT_SONG_PPQ): SongPosition {
  const safePpq = Math.max(1, ppq);
  const barBeats = musicalBeatsPerBar(signature);
  const absoluteBeats = tick / safePpq;
  if (tick < 0) {
    const withinPickup = ((absoluteBeats % barBeats) + barBeats) % barBeats;
    return {
      absoluteTick: tick,
      section: "PICKUP",
      bar: 0,
      beat: Math.floor(withinPickup) + 1,
      subdivision: withinPickup - Math.floor(withinPickup),
    };
  }
  const bar = Math.floor(absoluteBeats / barBeats) + 1;
  const withinBar = absoluteBeats - (bar - 1) * barBeats;
  return {
    absoluteTick: tick,
    section: "SONG",
    bar,
    beat: Math.floor(withinBar) + 1,
    subdivision: withinBar - Math.floor(withinBar),
  };
}

export function passRangeTicks(startBar: number, lengthBars: PassLengthBars, signature: MidiTimeSignature, ppq = DEFAULT_SONG_PPQ) {
  const barTicks = ticksPerBar(signature, ppq);
  const startTick = Math.max(0, Math.floor(startBar)) * barTicks;
  const endTick = lengthBars === null ? Number.POSITIVE_INFINITY : startTick + lengthBars * barTicks;
  return { startTick, endTick };
}

export function nextEmptyBar(notes: MidiNote[], signature: MidiTimeSignature, ppq = DEFAULT_SONG_PPQ): number {
  const barTicks = ticksPerBar(signature, ppq);
  const endTick = notes.length
    ? Math.max(0, ...notes.map((note) => Math.round((note.startBeat + note.durationBeats) * ppq)))
    : 0;
  return Math.max(0, Math.ceil(endTick / barTicks));
}

export function buildPassTapEvidence(input: {
  gestures: MelodyTapRawGesture[];
  clock: MusicalClock;
  gridBeats: number;
  pickup: boolean;
}): PassTapEvidence[] {
  const gridTicks = Math.max(1, Math.round(input.gridBeats * input.clock.ppq));
  return [...input.gestures]
    .sort((left, right) => left.startTime - right.startTime || left.id - right.id)
    .map((gesture) => {
      const rawStartTick = performanceTimeToAbsoluteTick(input.clock, gesture.startTime);
      const rawEndTick = Math.max(rawStartTick + 1, performanceTimeToAbsoluteTick(input.clock, gesture.endTime));
      const deviceCorrectedStartTick = correctedPerformanceTimeToAbsoluteTick(input.clock, gesture.startTime);
      const deviceCorrectedEndTick = Math.max(deviceCorrectedStartTick + 1, correctedPerformanceTimeToAbsoluteTick(input.clock, gesture.endTime));
      const allowedStart = input.pickup ? deviceCorrectedStartTick : Math.max(input.clock.regionStartTick, deviceCorrectedStartTick);
      const quantizedStartTick = Math.round(allowedStart / gridTicks) * gridTicks;
      const quantizedEndTick = Math.max(quantizedStartTick + 1, Math.round(Math.max(allowedStart + 1, deviceCorrectedEndTick) / gridTicks) * gridTicks);
      return {
        id: gesture.id,
        rawStartTimeMs: gesture.startTime,
        rawEndTimeMs: gesture.endTime,
        rawStartTick,
        rawEndTick,
        deviceCorrectedStartTick,
        deviceCorrectedEndTick,
        quantizedStartTick,
        quantizedEndTick,
        startTick: quantizedStartTick,
        endTick: quantizedEndTick,
      };
    });
}

export function regionHasNotes(notes: MidiNote[], startTick: number, endTick: number, ppq = DEFAULT_SONG_PPQ): boolean {
  return notes.some((note) => {
    const noteStart = Math.round(note.startBeat * ppq);
    const noteEnd = Math.max(noteStart + 1, Math.round((note.startBeat + note.durationBeats) * ppq));
    return noteStart < endTick && noteEnd > startTick;
  });
}

export function mergeInputPassNotes(existingNotes: MidiNote[], pass: Pick<InputPass, "startTick" | "endTick" | "mergeMode" | "createdNotes" | "ppq">): MidiNote[] {
  const earliestCreatedTick = pass.createdNotes.length
    ? Math.min(...pass.createdNotes.map((note) => Math.round(note.startBeat * pass.ppq)))
    : pass.startTick;
  const replaceStart = Math.min(pass.startTick, earliestCreatedTick);
  const kept = pass.mergeMode === "REPLACE_RANGE"
    ? existingNotes.filter((note) => {
      const noteStart = Math.round(note.startBeat * pass.ppq);
      const noteEnd = Math.max(noteStart + 1, Math.round((note.startBeat + note.durationBeats) * pass.ppq));
      return noteEnd <= replaceStart || noteStart >= pass.endTick;
    })
    : existingNotes;
  return [...kept.map((note) => ({ ...note })), ...pass.createdNotes.map((note) => ({ ...note }))]
    .sort((left, right) => left.startBeat - right.startBeat || left.midi - right.midi);
}

export function createInputPass(input: Omit<InputPass, "id" | "source" | "createdAt"> & { id?: string; createdAt?: string }): InputPass {
  return {
    ...input,
    id: input.id ?? `pass-${Date.now().toString(36)}`,
    source: "ADD_PASS",
    createdAt: input.createdAt ?? new Date().toISOString(),
    startTick: Math.round(input.startTick),
    endTick: Number.isFinite(input.endTick) ? Math.max(Math.round(input.startTick) + 1, Math.round(input.endTick)) : input.endTick,
    audioOffsetMs: Number.isFinite(input.audioOffsetMs) ? Math.max(-500, Math.min(500, Math.round(input.audioOffsetMs))) : 0,
    f0Evidence: input.f0Evidence.map((frame) => ({ ...frame })),
    tapEvents: input.tapEvents.map((event) => ({ ...event })),
    rhythmSlots: input.rhythmSlots.map((slot) => ({ ...slot })),
    createdNotes: input.createdNotes.map((note) => ({ ...note })),
  };
}

export function suppressCountClickTransient<T extends PitchFrame>(frame: T, audioTime: number, clickTimes: number[], headphones = false): T {
  if (headphones) return frame;
  const distance = clickTimes.length ? Math.min(...clickTimes.map((time) => Math.abs(audioTime - time))) : Number.POSITIVE_INFINITY;
  if (distance > 0.065) return frame;
  return {
    ...frame,
    confidence: clamp(frame.confidence * 0.68, 0, 1),
    acceptanceConfidence: Math.max(frame.acceptanceConfidence ?? 0, 0.72),
  };
}
